import { useEffect, useRef, useState, type FormEvent } from 'react';
import { PRODUCT_LINES, type ProductLine } from '@nava/product-lines';
import { X } from 'lucide-react';
import { updateBackofficeProjectProductLine } from '../api';
import { formatProductLine } from '../format';
import type { BackofficeProjectDetail } from '../types';

export interface ChangeProjectProductLineModalProps {
  project: BackofficeProjectDetail;
  onClose: () => void;
  onUpdated: (project: BackofficeProjectDetail) => void;
}

export function ChangeProjectProductLineModal({
  project,
  onClose,
  onUpdated,
}: ChangeProjectProductLineModalProps) {
  const [productLine, setProductLine] = useState<ProductLine | ''>(project.productLine ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    return () => {
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!productLine || productLine === project.productLine || saving || project.deletedAt) return;
    setSaving(true);
    setError(null);
    try {
      const response = await updateBackofficeProjectProductLine(project.id, { productLine });
      onUpdated(response.project);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to update product line');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-product-line-title"
        className="w-full max-w-lg overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
        onSubmit={handleSubmit}
        onKeyDown={(event) => {
          if (event.key === 'Tab') {
            const controls = Array.from(
              event.currentTarget.querySelectorAll<HTMLElement>(
                'button:not(:disabled), select:not(:disabled)'
              )
            );
            const first = controls[0];
            const last = controls.at(-1);
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
          if (event.key === 'Escape' && !saving) {
            event.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <h2 id="change-product-line-title" className="text-title font-semibold">
            Change product line
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            disabled={saving}
            onClick={onClose}
            className="focus-ring rounded-md p-1 text-neutral-500"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          {error ? (
            <p role="alert" className="text-small text-red-700">
              {error}
            </p>
          ) : null}
          <label htmlFor="study-product-line" className="block text-small font-semibold">
            Product line
          </label>
          <select
            id="study-product-line"
            value={productLine}
            disabled={saving}
            onChange={(event) => {
              setProductLine(event.target.value as ProductLine);
              setError(null);
            }}
            className="focus-ring block w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm"
          >
            {!productLine ? (
              <option value="" disabled>
                Select a product line
              </option>
            ) : null}
            {PRODUCT_LINES.map((line) => (
              <option key={line} value={line}>
                {formatProductLine(line)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="focus-ring rounded-lg px-4 py-2 text-small font-semibold"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              !productLine || productLine === project.productLine || saving || !!project.deletedAt
            }
            className="focus-ring rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
